import { Link, useLocation } from 'react-router-dom';
import { Anvil, Menu, X } from 'lucide-react';
import { useState } from 'react';
import clsx from 'clsx';

const navLinks = [
  { path: '/', label: 'Home' },
  { path: '/first-day', label: 'First Day' },
  { path: '/try-it', label: 'Try It' },
  { path: '/wizard', label: 'Wizard' },
  { path: '/templates', label: 'Templates' },
  { path: '/playbooks', label: 'Playbooks' },
  { path: '/generations', label: 'Generations' },
];

export default function Navbar() {
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <header className="fixed top-0 left-0 right-0 z-50">
      <nav className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div
          className={clsx(
            'mt-3 flex items-center justify-between rounded-2xl px-6 py-3',
            'bg-surface-DEFAULT/60 backdrop-blur-2xl border border-dark-border',
            'shadow-lg shadow-black/20'
          )}
        >
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-2.5 group"
          >
            <div className="relative">
              <Anvil className="h-7 w-7 text-accent transition-transform duration-300 group-hover:scale-110" />
              <div className="absolute inset-0 bg-accent/20 blur-xl rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </div>
            <span className="text-lg font-bold tracking-tight text-white">
              Code<span className="text-accent">Forge</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => {
              const isActive =
                link.path === '/'
                  ? location.pathname === '/'
                  : location.pathname.startsWith(link.path);
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  className={clsx(
                    'relative px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200',
                    isActive
                      ? 'text-white'
                      : 'text-gray-400 hover:text-gray-200 hover:bg-surface-100/50'
                  )}
                >
                  {isActive && (
                    <div className="absolute inset-0 bg-surface-100 rounded-lg" />
                  )}
                  <span className="relative z-10">{link.label}</span>
                  {isActive && (
                    <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-4 h-0.5 bg-accent rounded-full" />
                  )}
                </Link>
              );
            })}
          </div>

          {/* Desktop CTA */}
          <div className="hidden md:block">
            <Link
              to="/wizard"
              className="btn-primary text-sm py-2 px-4 rounded-lg"
            >
              Get Started
            </Link>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            className="md:hidden p-2 text-gray-400 hover:text-white transition-colors"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileOpen && (
          <div className="md:hidden mt-2 rounded-2xl bg-surface-DEFAULT/95 backdrop-blur-2xl border border-dark-border p-4 shadow-xl">
            <div className="flex flex-col gap-1">
              {navLinks.map((link) => {
                const isActive =
                  link.path === '/'
                    ? location.pathname === '/'
                    : location.pathname.startsWith(link.path);
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setMobileOpen(false)}
                    className={clsx(
                      'px-4 py-3 text-sm font-medium rounded-lg transition-colors',
                      isActive
                        ? 'text-white bg-surface-100'
                        : 'text-gray-400 hover:text-gray-200 hover:bg-surface-50'
                    )}
                  >
                    {link.label}
                  </Link>
                );
              })}
              <div className="mt-2 pt-2 border-t border-dark-border">
                <Link
                  to="/wizard"
                  onClick={() => setMobileOpen(false)}
                  className="btn-primary w-full text-sm"
                >
                  Get Started
                </Link>
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}
